import { useEffect, useState } from "react";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { Checkbox, Textarea } from "../ui";
import { useSession } from "./session";
import type { PluginManifest } from "../registry/pluginLoader";

/**
 * Settings → Plugins (spec §9.10, level `power`): every discovered plugin with one switch and its
 * config as JSON. Enabling or disabling never deletes data.
 */
export function PluginSettings() {
  const { t } = useTranslation();
  const isAdmin = useSession((s) => s.session?.profile?.isAdmin);
  const [plugins, setPlugins] = useState<PluginManifest[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [bad, setBad] = useState<Record<string, boolean>>({});

  const refresh = () =>
    fetch("/api/plugins?all=1", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((b: { plugins: PluginManifest[] }) => setPlugins(b.plugins));
  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);
  if (!isAdmin) return null;

  const patch = (id: string, body: Record<string, unknown>) =>
    fetch(`/api/internal/plugins/${id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(() => refresh());

  return (
    <SettingsSection
      id="settings-plugins"
      intro={t("plugins.hint")}
      summary={t("settings.summary.plugins")}
      title={t("plugins.title")}
      testId="settings-plugins"
      folded
    >
      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
        {plugins.map((p) => (
          <li key={p.id} className="flex flex-col gap-2 py-2.5" data-testid="plugin-row" data-plugin={p.id}>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={p.enabled}
                label={t("plugins.enable", { id: p.id })}
                onChange={(v) => void patch(p.id, { enabled: v })}
              />
              <span className="font-mono text-[13px]">{p.id}</span>
              <StatusText tone="quiet">{p.kind.join(", ")}</StatusText>
              {p.builtin && <StatusText tone="accent">{t("plugins.builtin")}</StatusText>}
              <span className="ml-auto font-mono text-[11px] text-neutral-500">{p.version}</span>
            </div>
            {p.enabled && hasForm(p) && (
              <a href="#settings-integrations" className="text-[12.5px] text-accent underline">
                {t("plugins.inIntegrations")}
              </a>
            )}
            {/* raw JSON only for a plugin without a form (UI cleanup U5): Integrations edits the others */}
            {p.enabled && !hasForm(p) && Object.keys(p.configSchema?.properties ?? {}).length > 0 && (
              <Textarea
                rows={3}
                spellCheck={false}
                aria-label={t("plugins.config", { id: p.id })}
                className={`font-mono text-[12px] ${bad[p.id] ? "border-err" : ""}`}
                value={drafts[p.id] ?? JSON.stringify(p.config ?? {}, null, 1)}
                onChange={(e) => setDrafts({ ...drafts, [p.id]: e.target.value })}
                onBlur={() => {
                  const text = drafts[p.id];
                  if (text === undefined) return;
                  try {
                    void patch(p.id, { config: JSON.parse(text || "{}") });
                    setBad({ ...bad, [p.id]: false });
                  } catch {
                    setBad({ ...bad, [p.id]: true });
                  }
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </SettingsSection>
  );
}

/** Whether Settings → Integrations has a form for this plugin's configuration (`x-drang: "connections"`). */
function hasForm(p: PluginManifest): boolean {
  const props = (p.configSchema as { properties?: Record<string, Record<string, unknown>> } | undefined)
    ?.properties;
  return props?.connections?.["x-drang"] === "connections";
}
